'use client';

import { createContext, useMemo, useContext, useCallback } from 'react';
import useLoginModal from '@fe-news/hooks/use-login-modal';
import useMyCollection from '@fe-news/hooks/use-my-collection';

// type
import { MyCollectionProductType } from '@fe-news/constants/my-track/type';

type NewsCollectContextValue = {
  isLoggedIn: boolean;
  trackingList: string[];
  isLoading: boolean;
  isCloseLoginModal: boolean;
  refetch: () => Promise<void>;
  onClickLoginModalOpen: () => void;
  onClickLoginModalClose: () => void;
};

const NewsCollectContext = createContext<NewsCollectContextValue | undefined>(undefined);

export default function NewsCollectProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, isCloseLoginModal, onClickLoginModalOpen, onClickLoginModalClose } = useLoginModal({
    defaultCloseLoginModal: false
  });

  const {
    myCollectionDataItemsIdArray,
    isLoading: isLoadingMyCollection,
    refetch
  } = useMyCollection({
    productType: MyCollectionProductType.NEWS,
    isLogin: isLoggedIn
  });

  const onClickRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo(
    () => ({
      isLoggedIn,
      isCloseLoginModal,
      onClickLoginModalOpen,
      onClickLoginModalClose,
      trackingList: myCollectionDataItemsIdArray,
      isLoading: isLoadingMyCollection,
      refetch: onClickRefetch
    }),
    [
      isLoggedIn,
      myCollectionDataItemsIdArray,
      isLoadingMyCollection,
      isCloseLoginModal,
      refetch,
      onClickLoginModalOpen,
      onClickLoginModalClose
    ]
  );

  return <NewsCollectContext.Provider value={value}>{children}</NewsCollectContext.Provider>;
}

export const useNewsCollect = () => {
  const context = useContext(NewsCollectContext);
  if (!context) {
    throw new Error('useNewsCollect must be used within a NewsCollectProvider');
  }
  return context;
};
